import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import CountUp from "react-countup";
import { Gift, Copy, Users, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

export default function ReferralCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const referralLink = `${window.location.origin}/register?ref=${data.referral_code}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success("Referral link copied!");
    } catch (e) {
      toast.error(`Could not copy automatically — copy manually: ${referralLink}`);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-6 border border-emerald-400/15 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-emerald-400/30 hover:shadow-[0_12px_40px_rgba(0,0,0,0.35)]" data-testid="referral-card">
      <h2 className="font-heading font-semibold text-white mb-1 flex items-center gap-2">
        <span className="w-6 h-6 rounded-md bg-emerald-400/10 flex items-center justify-center"><Gift className="w-3.5 h-3.5 text-emerald-400" /></span> Refer a Colleague
      </h2>
      <p className="text-xs text-slate-400 mb-4">
        Share your link — you both earn +{data.reward_xp} XP and +{data.reward_coins} coins when they complete Day 1.
      </p>

      <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-lg px-3 py-2.5 mb-4">
        <span className="text-xs text-slate-300 flex-1 truncate" data-testid="referral-link-text">{referralLink}</span>
        <button onClick={copyLink} data-testid="copy-referral-link-btn" className="flex items-center gap-1.5 text-xs font-semibold text-indigo-400 hover:text-indigo-300 shrink-0 transition-colors">
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 text-center">
        <div data-testid="referral-stat-total">
          <p className="font-heading text-lg font-bold text-white flex items-center justify-center gap-1 tabular-nums"><Users className="w-3.5 h-3.5 text-slate-400" /><CountUp end={data.total_referred} duration={1.2} /></p>
          <p className="text-[10px] text-slate-400 mt-0.5">Referred</p>
        </div>
        <div data-testid="referral-stat-completed">
          <p className="font-heading text-lg font-bold text-emerald-400 flex items-center justify-center gap-1 tabular-nums"><CheckCircle2 className="w-3.5 h-3.5" /><CountUp end={data.total_completed} duration={1.2} /></p>
          <p className="text-[10px] text-slate-400 mt-0.5">Completed</p>
        </div>
        <div data-testid="referral-stat-xp">
          <p className="font-heading text-lg font-bold text-[#FBBF24] tabular-nums">+<CountUp end={data.xp_earned} duration={1.2} /></p>
          <p className="text-[10px] text-slate-400 mt-0.5">XP Earned</p>
        </div>
      </div>

      {data.referrals?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/[0.06] space-y-2">
          {data.referrals.slice(0, 5).map((r, i) => (
            <div key={i} className="flex items-center justify-between text-xs" data-testid={`referral-row-${i}`}>
              <span className="text-slate-300 truncate">{r.name}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.status === "completed" ? "bg-emerald-400/10 text-emerald-400" : "bg-amber-400/10 text-amber-400"}`}>
                {r.status === "completed" ? "Rewarded" : "Pending"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
